import React, { useContext, useRef } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Link, Text } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { ComplianceIcon, ComplianceServicesL2Icon, DocumentLegalDocsIcon } from '@fiverr-private/visuals';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { translate } from '@fiverr-private/i18n-react';
import { ComplianceContext } from '../ComplianceProvider';
import { reportEventsApi } from '../../biEvents';
import { useSellerConfigData } from '../../api/useSellerConfigData';
import { ComplianceType, ComplianceTypes } from '../ComplianceModal/types';
import { AgencyFirstTimeComplianceCard } from './AgencyFirstTimeComplianceCard';

interface AgencyFirstTimeComplianceSectionProps {
    viewType?: string;
    requeseteeId: number;
    requeseteeUserName: string;
}

export const AgencyFirstTimeComplianceSection: React.FC<AgencyFirstTimeComplianceSectionProps> = ({
    viewType = '',
    requeseteeId,
    requeseteeUserName,
}) => {
    const complianceContext = useContext(ComplianceContext);
    const { sellerConfigs } = useSellerConfigData(requeseteeId);

    const handleBoxClick = async (complianceType: ComplianceType) => {
        if (complianceType === ComplianceTypes.LEGAL_DOCS) {
            complianceContext?.openSendLegalDocsModal({ sellerName: requeseteeUserName, sellerId: requeseteeId });
        }
    };

    const observedElementRef = useRef(null);

    const complianceItems: {
        icon: React.ReactNode;
        complianceType: ComplianceType;
        Wrapper: React.FC<{ children: React.ReactNode }>;
    }[] = [
        {
            icon: <DocumentLegalDocsIcon size={'md'} color={'grey_1200'} />,
            complianceType: ComplianceTypes.LEGAL_DOCS,
            Wrapper: ({ children }) => (
                <Container width="100%">
                    <Link
                        cursor="pointer"
                        href={redirectUrl}
                        onClick={() => {
                            if (complianceContext) {
                                reportEventsApi.clickOnLegalDocument({
                                    pageName: complianceContext.pageName,
                                    type: 'section',
                                });
                            }

                            if (!redirectUrl) {
                                complianceContext?.openSendLegalDocsModal({
                                    sellerName: requeseteeUserName,
                                    sellerId: requeseteeId,
                                });
                            }
                        }}
                        target="_blank"
                        rel="noreferrer"
                        decoration="none"
                    >
                        {children}
                    </Link>
                </Container>
            ),
        },
    ];

    const legalDocsConfigurations = sellerConfigs?.legalDocsData;

    const isAllowedToUpload = legalDocsConfigurations?.allowedActions?.isAllowedToUpload;
    const totalTemplates = complianceContext?.complianceData?.legalDocsData?.totalTemplates;
    const redirectUrl = isAllowedToUpload && !totalTemplates ? pathfinder('legal_documents_page_render') : undefined;

    const { isMobile } = getContext();
    const smallView = isMobile || viewType === 'inbox';

    return (
        <Stack
            gap="0"
            ref={observedElementRef}
            backgroundColor="grey_100"
            borderRadius="lg"
            borderColor="grey_400"
            borderWidth="sm"
            paddingY="6"
            paddingX="5"
            borderStyle="solid"
            width="100%"
            direction={viewType === 'inbox' ? 'column' : 'row'}
            alignItems="center"
            justifyContent="spaceBetween"
        >
            <Stack
                direction="row"
                alignItems={viewType === 'inbox' ? 'flexStart' : 'center'}
                gap={viewType === 'inbox' ? '2' : '4'}
                flexGrow={1}
            >
                <Box display="inlineFlex">
                    {viewType === 'inbox' ? <ComplianceIcon size="lg" /> : <ComplianceServicesL2Icon size="md" />}
                </Box>
                <Stack direction="column" gap="1">
                    <Text size="b_md" fontWeight="bold">
                        {translate('compliance.agency_first_time_compliance_section.title')}
                    </Text>
                    {viewType !== 'inbox' && (
                        <Text size="b_sm" color="bodySecondary">
                            {translate('compliance.agency_first_time_compliance_section.body')}
                        </Text>
                    )}
                </Stack>
            </Stack>

            <Box
                marginTop={'2'}
                marginLeft={smallView ? 'auto' : '5'}
                borderColor={'grey_400'}
                borderWidth={'sm'}
                borderStyle={'solid'}
                borderRadius={'lg'}
                width={smallView ? '100%' : 'auto'}
            >
                {complianceItems.map(({ Wrapper, ...item }) => (
                    <Wrapper key={item.complianceType}>
                        <AgencyFirstTimeComplianceCard
                            isCompact={smallView}
                            onClick={() => handleBoxClick(item.complianceType)}
                        />
                    </Wrapper>
                ))}
            </Box>
        </Stack>
    );
};
